import React, { useMemo, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { ContactDetailsType } from '../../../../../../base/enums';
import { ContactDetails, PhoneContactDetails } from '../../../../../../base/interfaces/general';
import { __t } from '../../../../../../base/utils/t';
import { ReactComponent as TelegramIcon } from '../../../../../../base/assets/img/telegram.svg';
import { ReactComponent as ViberIcon } from '../../../../../../base/assets/img/viber.svg';
import { ReactComponent as WhatsAppIcon } from '../../../../../../base/assets/img/whatsapp.svg';
import { ClickTooltip } from '../../../../../../base/components/ClickTooltip';
import { Icon } from '../../../../../../base/components/Icon';
import { useIsMobile } from '../../../../../../base/hooks';
import { Button } from '../../../../../../base/components/Button';

import './styles.scss';

interface Props {
    isUserDeleted: boolean;
    showContacts: boolean;
    contacts: ContactDetails[];
    messengerUrl: string;
    markNewResponseAsReadHandler: () => void;
}

export const ContactData = ({
    showContacts,
    contacts,
    messengerUrl,
    isUserDeleted,
    markNewResponseAsReadHandler,
}: Props) => {
    const isMobile = useIsMobile();
    const [showMoreContactsActive, setShowMoreContactsActive] = useState(false);

    const getIcon = (type: 'viber' | 'whatsapp' | 'telegram') => {
        switch (type) {
            case 'telegram':
                return (
                    <TelegramIcon
                        className='responses-table-contacts-data__messenger-icon'
                        width='15px'
                        height='15px'
                    />
                );
            case 'viber':
                return (
                    <ViberIcon className='responses-table-contacts-data__messenger-icon' width='15px' height='15px' />
                );
            case 'whatsapp':
                return (
                    <WhatsAppIcon
                        className='responses-table-contacts-data__messenger-icon'
                        width='15px'
                        height='15px'
                    />
                );
            default:
                console.error(`Unsupported messenger ${type} type provided!`);
        }
    };

    const messengerButton = useMemo(() => {
        if (!messengerUrl) {
            return null;
        }

        if (isMobile) {
            const clickHandler = () => {
                window.open(messengerUrl, '_blank', 'noreferrer');
                markNewResponseAsReadHandler();
            };

            return (
                <Button
                    type='secondary'
                    className='responses-table-contacts-data__messenger-button'
                    onClick={clickHandler}
                >
                    {__t('Написати повідомлення')}
                </Button>
            );
        }

        return (
            <a
                href={messengerUrl}
                className='responses-table-contacts-data__messenger-link'
                target='_blank'
                rel='noreferrer'
                onClick={markNewResponseAsReadHandler}
            >
                {__t('Написати повідомлення')}
            </a>
        );
    }, [messengerUrl, isMobile, markNewResponseAsReadHandler]);

    if (!showContacts) {
        return (
            <div className='responses-table-contacts-hidden'>
                {__t('Контактні дані будуть доступні після першого запрошення на вакансію.')}
            </div>
        );
    }

    const phoneContacts = contacts.filter((c) => c.type === ContactDetailsType.Phone) as PhoneContactDetails[];

    if (!phoneContacts.length || isUserDeleted) {
        return null;
    }

    const firtsContact = phoneContacts[0];

    const showMoreContactsClickHandler = () => {
        markNewResponseAsReadHandler();
        setShowMoreContactsActive(!showMoreContactsActive);
    };

    const closeShowMoreContactsCallback = () => {
        setShowMoreContactsActive(false);
    };

    return (
        <div className='responses-table-contacts-data'>
            <div className='responses-table-contacts-data__item'>
                <a href={`tel:${firtsContact.phone}`} onClick={markNewResponseAsReadHandler}>
                    {firtsContact.phone}
                </a>
                {firtsContact.isViber ? getIcon('viber') : null}
                {firtsContact.isWhatsapp ? getIcon('whatsapp') : null}
                {firtsContact.isTelegram ? getIcon('telegram') : null}
                {phoneContacts.length > 1 ? (
                    <ClickTooltip
                        align='center'
                        wrapperClassName='responses-table-contacts-data__tooltip-wrapper'
                        className='responses-table-interactions__tooltip-component'
                        activeButton={
                            <div onClick={showMoreContactsClickHandler} className='responses-table-interactions__tooltip-icon-wrapper'>
                                <Icon
                                    icon='angles-right-regular'
                                    size='14px'
                                    color={showMoreContactsActive ? 'default' : 'info'}
                                    className='responses-table-contacts-data__tooltip-icon'
                                />
                            </div>
                        }
                        closeTooltipCallback={closeShowMoreContactsCallback}
                    >
                        <>
                            {phoneContacts.map((i, index) => (
                                <div
                                    className={`responses-table-contacts-data__item ${
                                        index === phoneContacts.length - 1
                                            ? 'responses-table-contacts-data__item--last'
                                            : ''
                                    }`}
                                    key={uuid()}
                                >
                                    <a href={`tel:${i.phone}`}>{i.phone}</a>
                                    {i.isViber ? getIcon('viber') : null}
                                    {i.isWhatsapp ? getIcon('whatsapp') : null}
                                    {i.isTelegram ? getIcon('telegram') : null}
                                </div>
                            ))}
                        </>
                    </ClickTooltip>
                ) : null}
            </div>
            {messengerButton}
        </div>
    );
};
